import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const MeatProductPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inquiryMsg, setInquiryMsg] = useState('');
  const [inquirySent, setInquirySent] = useState(false);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [showInquiry, setShowInquiry] = useState(false);
  const [cartSuccess, setCartSuccess] = useState(false);
  const [qty, setQty] = useState(1);

  const API_URL = process.env.REACT_APP_API_URL;
  const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:4000';

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/public/meat_product/${id}`);
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        setProduct(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, API_URL]);

  const handleSendInquiry = async () => {
    if (!inquiryMsg.trim()) return;
    setInquiryLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) { alert('Please login to send an inquiry.'); return; }
      await fetch(`${API_URL}/api/customer/inquiries/meat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          product_id: product.id,
          seller_id:  product.butcher_id,
          message:    inquiryMsg,
        }),
      });
      setInquirySent(true);
      setInquiryMsg('');
    } catch {
      alert('Could not send inquiry. Please try again.');
    } finally {
      setInquiryLoading(false); }
  };

  const getImageUrl = (url) => {
    if (!url) return '/assets/placeholder.png';
    if (url.startsWith('http')) return url;
    return `${BASE_URL}${url}`;
  };

  if (loading) return (
    <div className="bg-background min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="text-on-surface-variant font-medium">Loading...</p>
      </div>
    </div>
  );

  if (error || !product) return (
    <div className="bg-background min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <p className="text-on-surface font-bold text-xl">Product not found</p>
        <button onClick={() => navigate('/search?mode=meat')}
          className="px-6 py-3 bg-primary text-white rounded-full font-bold">
          Back to Marketplace
        </button>
      </div>
    </div>
  );

  const isOutOfStock = product.stock_qty <= 0;
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwner = userData?.id && String(userData.id) === String(product.butcher_id);

  const handleAddToCart = async (buyNow = false) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData.token) {
      sessionStorage.setItem('pending_cart_item', JSON.stringify({ id: product.id }));
      navigate('/login');
      return;
    }
    try {
      await fetch(`${API_URL}/api/customer/cart/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userData.token}`,
        },
        body: JSON.stringify({
          items: [{
            id:        product.id,
            name:      product.name,
            price:     Number(product.price_inr),
            image:     product.image_url || null,
            category:  product.category,
            seller_id: product.butcher_id,
            quantity:  qty,
            stock:     product.stock_qty,
          }]
        }),
      });
    } catch (err) {
      console.error('Cart sync failed:', err);
    }
    if (buyNow) navigate('/customer#cart');
    else {
      setCartSuccess(true);
      setTimeout(() => setCartSuccess(false), 3000);
    }
  };

  return (
    <div className="bg-background text-on-surface font-body">
      <Navbar variant="default" />

      <main className="pt-28 pb-20 max-w-screen-2xl mx-auto px-6 md:px-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 mb-8 text-sm font-medium text-on-secondary-container">
          <a onClick={() => navigate('/')} className="hover:text-primary cursor-pointer">Home</a>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <a onClick={() => navigate('/search')} className="hover:text-primary cursor-pointer">Marketplace</a>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <a onClick={() => { navigate('/search'); setTimeout(() => {}, 100); }}
            className="hover:text-primary cursor-pointer">Meat Products</a>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <span className="text-on-surface">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left: Image + Info */}
          <div className="lg:col-span-8 space-y-8">
            {/* Main Image */}
            <div className="relative group overflow-hidden rounded-2xl bg-surface-container h-[500px]">
              <img
                src={getImageUrl(product.image_url)}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

              {/* Category badge */}
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase text-primary">
                {product.category}
              </div>

              {/* Stock badge */}
              <div className={`absolute top-4 right-4 px-4 py-2 rounded-full text-sm font-bold backdrop-blur-md ${
                isOutOfStock ? 'bg-orange-500 text-white' : 'bg-black/60 text-white'
              }`}>
                {isOutOfStock ? 'Out of Stock' : `Stock: ${product.stock_qty}`}
              </div>
            </div>

            {/* Product Info */}
            <section className="space-y-8">
              <div className="border-b border-outline-variant pb-8">
                <h1 className="text-5xl font-extrabold tracking-tight text-on-surface mb-4">{product.name}</h1>
                {product.description && (
                  <p className="text-xl text-on-surface-variant font-light max-w-2xl leading-relaxed">
                    {product.description}
                  </p>
                )}
              </div>

              {/* Specs Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-surface-container-low p-6 rounded-xl border border-transparent hover:border-primary-container transition-all">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-on-secondary-container font-bold">Category</span>
                  <p className="text-2xl font-black mt-1 text-primary">{product.category || '—'}</p>
                </div>
                <div className="bg-surface-container-low p-6 rounded-xl border border-transparent hover:border-primary-container transition-all">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-on-secondary-container font-bold">Weight</span>
                  <p className="text-2xl font-black mt-1 text-primary">
                    {product.weight_kg ? `${product.weight_kg} ${product.unit || 'kg'}` : '—'}
                  </p>
                </div>
                <div className="bg-surface-container-low p-6 rounded-xl border border-transparent hover:border-primary-container transition-all">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-on-secondary-container font-bold">Stock</span>
                  <p className={`text-2xl font-black mt-1 ${isOutOfStock ? 'text-orange-500' : 'text-primary'}`}>
                    {isOutOfStock ? 'Out' : product.stock_qty}
                  </p>
                </div>
                <div className="bg-surface-container-low p-6 rounded-xl border border-transparent hover:border-primary-container transition-all">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-on-secondary-container font-bold">Location</span>
                  <p className="text-lg font-black mt-1 text-primary leading-tight">
                    {[product.seller_city, product.seller_state].filter(Boolean).join(', ') || '—'}
                  </p>
                </div>
              </div>

              {/* Out of stock notice */}
              {isOutOfStock && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center gap-3">
                  <span className="material-symbols-outlined text-orange-500">info</span>
                  <p className="text-sm font-bold text-orange-800">
                    This product is currently out of stock. You can still send an inquiry to the butcher.
                  </p>
                </div>
              )}
            </section>
          </div>

          {/* Right: Pricing & Seller */}
          <div className="lg:col-span-4">
            <div className="sticky top-28 space-y-6">
              {/* Pricing Card */}
              <div className="bg-surface-container-lowest editorial-shadow rounded-2xl p-8 border border-outline-variant/20">
                <div className="flex justify-between items-baseline mb-6">
                  <span className="text-on-secondary-container font-medium">Price</span>
                  <span className="text-4xl font-black text-primary tracking-tight">
                    ₹{Number(product.price_inr).toLocaleString('en-IN')}
                  </span>
                </div>

                {product.unit && (
                  <p className="text-xs text-on-surface-variant mb-4 -mt-4">
                    per {product.unit}
                    {product.weight_kg ? ` · ${product.weight_kg} ${product.unit}` : ''}
                  </p>
                )}

                <div className="space-y-3">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-sm font-semibold text-secondary">Qty:</span>
                    <div className="flex items-center gap-2 bg-surface-container rounded-xl px-3 py-1.5">
                      <button onClick={() => setQty(q => Math.max(1, q - 1))}
                        className="w-7 h-7 flex items-center justify-center font-bold text-secondary">−</button>
                      <span className="w-8 text-center font-black text-sm">{qty}</span>
                      <button onClick={() => setQty(q => Math.min(product.stock_qty || 99, q + 1))}
                        className="w-7 h-7 flex items-center justify-center font-bold text-secondary">+</button>
                    </div>
                    {qty >= product.quantity && (
                      <p className="text-xs text-orange-600 font-semibold">
                        Only {product.quantity} available
                      </p>
                    )}
                    {qty > 1 && (
                      <span className="text-sm font-black text-primary">
                        = ₹{(Number(product.price_inr) * qty).toLocaleString('en-IN')}
                      </span>
                    )}
                  </div>
                  {isOwner ? (
                    <div className="bg-surface-container rounded-xl px-4 py-3 text-sm text-secondary text-center">
                      This is your listing — you cannot purchase your own product.
                    </div>
                  ) : (
                    <>
                      <button onClick={() => handleAddToCart(false)} disabled={isOutOfStock}
                        className="w-full bg-primary text-on-primary font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all hover:bg-primary/90 active:scale-[0.98] shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed">
                        <span className="material-symbols-outlined">shopping_cart</span>
                        {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
                      </button>
                      {cartSuccess && <p className="text-center text-green-600 font-semibold text-sm">✓ Added to cart!</p>}
                      <button onClick={() => handleAddToCart(true)} disabled={isOutOfStock}
                        className="w-full bg-amber-500 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all hover:bg-amber-600 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed">
                        <span className="material-symbols-outlined">bolt</span>
                        Buy Now
                      </button>
                    </>
                  )}

                  {!isOwner && (
                    <>
                      <button
                        onClick={() => { setShowInquiry(true); setInquirySent(false); }}
                        className="w-full border-2 border-primary text-primary font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all hover:bg-primary/5 active:scale-[0.98]">
                        <span className="material-symbols-outlined">forum</span>
                        {isOutOfStock ? 'Ask About Availability' : 'Send Inquiry'}
                      </button>
                      <button
                        onClick={() => {
                          let phone = product.seller_phone?.replace(/\D/g, '');
                          if (!phone) { alert('Phone number not available.'); return; }
                          if (!phone.startsWith('91')) phone = '91' + phone;
                          const msg = encodeURIComponent(`Hi, I'm interested in your product: ${product.name}`);
                          window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
                        }}
                        className="w-full bg-[#25D366] text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all hover:bg-[#20bd5a] active:scale-[0.98]">
                        <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                          <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.316 1.592 5.43 0 9.856-4.426 9.858-9.855.002-5.43-4.425-9.856-9.853-9.856-5.417 0-9.856 4.425-9.859 9.854-.001 2.09.547 3.567 1.461 5.145l-1.08 3.945 4.107-1.08z"/>
                        </svg>
                        Chat on WhatsApp
                      </button>
                    </>
                  )}
                </div>

                <div className="mt-6 flex items-center justify-center gap-2 text-on-secondary-container text-xs font-semibold">
                  <span className="material-symbols-outlined text-sm text-primary">verified_user</span>
                  Verified Butcher Product
                </div>
              </div>

              {/* Seller Card */}
              <div className="bg-surface-container editorial-shadow rounded-2xl p-6">
                <h3 className="text-[10px] uppercase tracking-[0.2em] text-on-secondary-container font-black mb-5">
                  Butcher Information
                </h3>
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src={product.seller_image
                      ? (product.seller_image.startsWith('http') ? product.seller_image : `${BASE_URL}${product.seller_image}`)
                      : `https://ui-avatars.com/api/?name=${encodeURIComponent(product.seller_name || 'B')}&background=06A788&color=fff&size=128`}
                    alt={product.seller_name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-white ring-1 ring-black/5"
                  />
                  <div>
                    <div className="flex items-center gap-1">
                      <h4 className="font-bold text-lg">{product.seller_name}</h4>
                      <span className="material-symbols-outlined text-primary text-lg">verified</span>
                    </div>
                    <p className="text-sm text-on-surface-variant flex items-center gap-1">
                      <span className="material-symbols-outlined text-sm">location_on</span>
                      {[product.seller_city, product.seller_state].filter(Boolean).join(', ') || '—'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-surface-container-high/50 py-3 rounded-xl">
                    <p className="text-[10px] uppercase text-on-surface-variant font-bold">Member Since</p>
                    <p className="font-black text-on-surface">
                      {product.seller_member_since ? new Date(product.seller_member_since).getFullYear() : '—'}
                    </p>
                  </div>
                  <div className="bg-surface-container-high/50 py-3 rounded-xl">
                    <p className="text-[10px] uppercase text-on-surface-variant font-bold">Deliveries</p>
                    <p className="font-black text-on-surface">{product.seller_total_sales || 0}</p>
                  </div>
                </div>
                <button
                    className="w-full text-primary font-bold py-3 border-2 border-primary/10 rounded-xl hover:bg-primary/5 transition-colors mt-4"
                    onClick={() => navigate(`/butcher/${product.butcher_public_id || product.butcher_id}`)}>
                    View Butcher Profile
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {product.related_listings && product.related_listings.length > 0 && (
          <section className="mt-24">
            <div className="flex justify-between items-end mb-10">
              <div>
                <h2 className="text-3xl font-extrabold tracking-tight">More from {product.seller_name}</h2>
                <p className="text-on-surface-variant mt-1">Other meat products from this butcher.</p>
              </div>
              <a onClick={() => navigate('/search?mode=meat')} 
                className="text-primary font-bold flex items-center gap-2 hover:gap-3 transition-all cursor-pointer group">
                See All <span className="material-symbols-outlined transition-transform group-hover:translate-x-1">arrow_forward</span>
              </a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {product.related_listings.map(item => (
                <div key={item.id}
                  onClick={() => navigate(`/meat-product/${item.public_id || item.id}`)}
                  className="group cursor-pointer">
                  <div className="relative overflow-hidden rounded-2xl h-64 mb-4 shadow-sm">
                    <img
                      src={getImageUrl(item.image_url)}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-md px-2 py-1 rounded-full text-[10px] font-black uppercase text-primary">
                      {item.category}
                    </div>
                  </div>
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-lg group-hover:text-primary transition-colors">{item.name}</h3>
                    <p className="font-black text-lg text-primary">₹{Number(item.price_inr).toLocaleString('en-IN')}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer variant="default" />

      {/* Inquiry Modal */}
      {showInquiry && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => { setShowInquiry(false); setInquirySent(false); }}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4"
            onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-black text-lg">Send Inquiry</h3>
                <p className="text-sm text-secondary">{product.name}</p>
                {isOutOfStock && (
                  <span className="text-xs text-orange-600 font-semibold">Currently out of stock</span>
                )}
              </div>
              <button onClick={() => { setShowInquiry(false); setInquirySent(false); }}
                className="w-8 h-8 rounded-full bg-surface-container flex items-center justify-center">
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>

            {inquirySent ? (
              <div className="text-center py-6 space-y-2">
                <span className="material-symbols-outlined text-[48px] text-emerald-500">check_circle</span>
                <p className="font-black text-on-surface">Inquiry Sent!</p>
                <p className="text-sm text-secondary">The butcher will reply soon.</p>
                <button onClick={() => { setShowInquiry(false); setInquirySent(false); }}
                  className="mt-2 px-6 py-2 rounded-xl bg-primary text-white font-bold text-sm">
                  Done
                </button>
              </div>
            ) : (
              <>
                <textarea
                  value={inquiryMsg}
                  onChange={e => setInquiryMsg(e.target.value)}
                  placeholder={isOutOfStock
                    ? "Ask about restocking, similar products, pricing..."
                    : "Ask about quantity, delivery, custom cuts, pricing..."}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-outline-variant/40 text-sm
                            outline-none focus:border-primary focus:ring-2 focus:ring-primary/10
                            resize-none transition-all" />
                <button
                  onClick={handleSendInquiry}
                  disabled={inquiryLoading || !inquiryMsg.trim()}
                  className="w-full py-3 rounded-xl bg-primary text-white font-bold
                            hover:opacity-90 disabled:opacity-50 transition-all">
                  {inquiryLoading ? 'Sending...' : 'Send Message'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MeatProductPage;